import React from "react";

export interface FlexParentProps {
  /** Align the flex items vertically */
  alignItems?: "stretch" | "flex-start" | "flex-end" | "center" | "baseline";

  /** With multiple lines of children, this tells the box how to space the various lines vertically */
  alignContent?:
    | "flex-start"
    | "flex-end"
    | "center"
    | "space-between"
    | "space-around"
    | "space-evenly"
    | "stretch";

  /** How to align each box relative to each other on a horizontal axis */
  justifyContent?:
    | "flex-start"
    | "flex-end"
    | "center"
    | "space-between"
    | "space-around"
    | "space-evenly";

  /** The direction to align the children */
  direction?: "row" | "row-reverse" | "column" | "column-reverse";

  /** Wrapping properties of the container */
  wrap?: "nowrap" | "wrap" | "wrap-reverse";

  /** Sets the gaps/gutters between rows and columns of the container */
  gap?: string;

  /** By default, flex children are ordered via the order they appear in the HTML. To change that, use this property */
  order?: number;

  /** Grow a child container. These values are all relative to one another */
  grow?: number;

  /** The same as grow, but with shrink */
  shrink?: number;

  /** Overrides the default `align-items` for this child */
  alignSelf?: "stretch" | "flex-start" | "flex-end" | "center" | "baseline";

  /** A CSS class name to apply to the flex wrapper */
  className?: string;

  /** A test ID for the div */
  testId?: string;

  /** Styles to pass to the div */
  style?: React.CSSProperties;

  /** Children elements */
  children?: any;
}

/**
 * Helper component to prevent having to constantly rewrite the same flex box
 * CSS over and over. **If you can use the grid instead of this, please do.**
 * But sometimes you need to reach for flexbox. This lets us do this without
 * having write a ton of bespoke CSS.
 *
 * The below properties correspond to the default flex box properties. For a
 * fuller understanding, see https://css-tricks.com/snippets/css/a-guide-to-flexbox
 */
export function Flex({
  alignItems,
  alignContent,
  justifyContent,
  direction,
  wrap,
  gap,
  order,
  grow,
  shrink,
  alignSelf,
  className = "",
  children = null,
  style = {},
  testId,
}: FlexParentProps) {
  style = { ...style, display: "flex" };

  if (alignItems) {
    style.alignItems = alignItems;
  }

  if (alignContent) {
    style.alignContent = alignContent;
  }

  if (justifyContent) {
    style.justifyContent = justifyContent;
  }

  if (direction) {
    style.flexDirection = direction;
  }

  if (wrap) {
    style.flexWrap = wrap;
  }

  if (gap) {
    style.gap = gap;
  }

  if (order) {
    style.order = order;
  }

  if (grow) {
    style.flexGrow = grow;
  }

  if (shrink) {
    style.flexShrink = shrink;
  }

  if (alignSelf) {
    style.alignSelf = alignSelf;
  }

  return (
    <div style={style} className={className} data-testid={testId}>
      {children}
    </div>
  );
}

export interface FlexChildProps {
  /** By default, flex children are ordered via the order they appear in the HTML. To change that, use this property */
  order?: number;

  /** Grow a child container. These values are all relative to one another. If you make one child 3, another 6, and another 1, child 3 will fill 30% of the space; child 6 will fill 60% of the space; and child 1 will fill only 10% of the space */
  grow?: number;

  /** The same as grow, but with shrink */
  shrink?: number;

  /** Overrides the default `align-items` for this child */
  alignSelf?: "stretch" | "flex-start" | "flex-end" | "center" | "baseline";

  /** A CSS class name to apply to the child */
  className?: string;

  /** A test ID for the div */
  testId?: string;

  /** Styles to pass to the div */
  style?: React.CSSProperties;

  /** Children elements */
  children?: any;
}

/**
 * A wrapper for adding flex-self properties to individual items under a
 * flexbox container.
 *
 * For a quick overview of the properties here, check out
 * https://css-tricks.com/snippets/css/a-guide-to-flexbox
 */
export function FlexChild({
  order,
  grow,
  shrink,
  alignSelf,
  className = "",
  testId,
  style = {},
  children,
}: FlexChildProps) {
  if (order !== undefined) {
    style.order = order;
  }

  if (grow !== undefined) {
    style.flexGrow = grow;
  }

  if (shrink !== undefined) {
    style.flexShrink = shrink;
  }

  if (alignSelf) {
    style.alignSelf = alignSelf;
  }

  return (
    <div style={style} className={className} data-testid={testId}>
      {children}
    </div>
  );
}
